import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { trackPromise } from 'react-promise-tracker';
import { DrawState, Peeps, PeepLayer, PeepKey } from '../../types';
import assets from '../../consume';

// Variant mappings for random generation
const variants = {
  hair: ['hair_default', 'hair_hats', 'hair_specials'],
  wear: ['wear_default', 'wear_brands', 'wear_casuals', 'wear_formals', 'wear_specials', 'wear_sports'],
  addon: ['addon_default', 'addon_flags', 'addon_masks', 'addon_stuffs']
};

const SKIN_COUNT = 5;

const initialState: DrawState = {
  canvas: null,
  matrix: [],
  gender: 0,
  looking: false,
  peeps: {
    '1gen': [],
    '2wear': [],
    '3hair': [],
    '4mouth': [],
    '5addon': [],
  },
  skin: { index: 0 },
  mouth: { index: 0 },
  hair: { index: 0, variant: 0 },
  wear: { index: 0, variant: 0 },
  addon: { index: 0, variant: 0 }
};

// Async thunk for posting image to Imgur
export const postImage = createAsyncThunk(
  'draw/postImage',
  async (formData: FormData) => {
    const { uploadToImgur } = await import('../../services/imgur');
    return trackPromise(uploadToImgur(formData));
  }
);

const drawSlice = createSlice({
  name: 'draw',
  initialState,
  reducers: {
    setCanvas: (state, action: PayloadAction<{ name: keyof DrawState; data: unknown }>) => {
      const { name, data } = action.payload;
      // Canvas is a non-serializable value, we handle it specially
      if (name === 'canvas') {
        // Note: canvas stored as reference, RTK will warn but it's intentional
        (state as Record<string, unknown>)[name] = data;
      } else {
        (state as Record<string, unknown>)[name] = data;
      }
    },
    setIndex: (state, action: PayloadAction<{ name: 'skin' | 'mouth' | 'hair' | 'wear' | 'addon'; index: 'index' | 'variant'; data: number }>) => {
      const { name, index, data } = action.payload;
      (state[name] as Record<string, number>)[index] = data;
    },
    setGender: (state, action: PayloadAction<0 | 1>) => {
      state.gender = action.payload;
    },
    setLook: (state, action: PayloadAction<boolean>) => {
      state.looking = action.payload;
    },
    setPeeps: (state, action: PayloadAction<Peeps>) => {
      state.peeps = action.payload;
    },
    setPeep: (state, action: PayloadAction<{ name: PeepKey; data: PeepLayer[] }>) => {
      const { name, data } = action.payload;
      state.peeps[name] = data;
    },
    setMatrix: (state, action: PayloadAction<number[][]>) => {
      state.matrix = action.payload;
    },
    setRandom: (state) => {
      const Json = assets as Record<string, unknown[][][]>;

      // Random gender
      const newGender = Math.random() < 0.5 ? 0 : 1;
      state.gender = newGender as 0 | 1;

      // Random skin (0-4)
      state.skin.index = Math.floor(Math.random() * SKIN_COUNT);

      // Random mouth
      const mouthCount = Json.mouth?.[newGender]?.length ?? 1;
      state.mouth.index = Math.floor(Math.random() * mouthCount);

      // Random hair (variant + index)
      const hairVariant = Math.floor(Math.random() * variants.hair.length);
      const hairKey = variants.hair[hairVariant];
      const hairCount = Json[hairKey]?.[newGender]?.length ?? 1;
      state.hair.variant = hairVariant;
      state.hair.index = Math.floor(Math.random() * hairCount);

      // Random wear (variant + index)
      const wearVariant = Math.floor(Math.random() * variants.wear.length);
      const wearKey = variants.wear[wearVariant];
      const wearCount = Json[wearKey]?.[newGender]?.length ?? 1;
      state.wear.variant = wearVariant;
      state.wear.index = Math.floor(Math.random() * wearCount);

      // Random addon (variant + index)
      const addonVariant = Math.floor(Math.random() * variants.addon.length);
      const addonKey = variants.addon[addonVariant];
      const addonCount = Json[addonKey]?.[newGender]?.length ?? 1;
      state.addon.variant = addonVariant;
      state.addon.index = Math.floor(Math.random() * addonCount);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(postImage.fulfilled, (_state, _action) => {
      // Image posted successfully, result available in action.payload
    });
  }
});

export const {
  setCanvas,
  setIndex,
  setGender,
  setLook,
  setPeeps,
  setPeep,
  setMatrix,
  setRandom
} = drawSlice.actions;

export default drawSlice.reducer;
